(ns hai.home.desktop.mini-apps.calendar
  "My Calendar mini-app card: today's meetings as a compact list, or a
   'Connect your calendar' advert over demo meetings when no calendar is linked.
   The meeting row and advert are shared with the mobile card via
   [[hai.home.calendar-card]]; state comes from [[hai.home.calendar]]. This card
   owns the connect modals since it is the desktop home's only calendar surface."
  (:require
   [hai.home.calendar :as calendar]
   [hai.home.calendar-card :as cal-card]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as mini-apps]
   [hai.mini-apps.add-pill :as add-pill]
   [hai.journal2.timeline :as tl]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [humbleai.belt.set :as set]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Card frame (pink header, purple ink accents from the shared card palette)
;; ---------------------------------------------------------------------------

(o/defstyled card mini-apps/card
  [:.app-header {:background t/--calendar}]
  [:.app-title-group
   {:display "inline-flex" :align-items "center" :gap "10px"}]
  [:.app-icon {:color t/--card-ink}]
  [:.app-title {:color t/--card-ink}]
  [:.app-action-btn
   {:color t/--card-ink}
   [:&:hover {:background t/--card-ink-06}]]
  ;; The ＋ Calendar pill: the deeper rose sibling of this header's tint, the
  ;; step the notes and tasks pills take from theirs.
  [add-pill/pill
   {:background t/--calendar-add-fill
    :color t/--card-ink}
   [:at-media {:hover "hover"}
    [#{:.add-action :.add-menu}
     [:&:hover {:background t/--card-ink-06}]]]])

;; ---------------------------------------------------------------------------
;; Panel
;; ---------------------------------------------------------------------------

(defn panel [{title :mini-app/title}]
  (r/with-let [!open (r/atom #{:upcoming})
               toggle-section (fn [id] (swap! !open set/toggle-membership id))]
    (let [meetings  @(<s [::calendar/timed-meetings])
          active?   @(<s [::calendar/any-cal-active?])
          gcal?     @(<s [::calendar/gcal-active?])
          loading?  (or @(<s [::calendar/loading?])
                        (not @(<s [::calendar/connection-status-initialized?])))
          collapsed? @(<s [::mini-apps-data/collapsed? :calendar])
          open      @!open
          past      (filter #(= :past (::calendar/status %)) meetings)
          upcoming  (remove #(= :past (::calendar/status %)) meetings)]
      [card {:class (when collapsed? "collapsed")}
       [tl/not-connected-modal]
       [tl/calendar-picker-modal]
       [:div.app-header
        (if active?
          ;; Connected: the title opens this week's events in the provider's
          ;; calendar, the way the old "See this week" link did.
          [:a.app-title-link
           {:href (calendar/calendar-week-url (if gcal? :google :microsoft-work))
            :target "_blank"
            :title "View this week's events in a new tab"}
           [:span.app-icon [icons/calendar {:size 18}]]
           [:h2.app-title title]]
          [:div.app-title-group
           [:span.app-icon [icons/calendar {:size 18}]]
           [:h2.app-title title]])
        [:div.app-actions
         [add-pill/component
          {:label      "Calendar"
           :aria-label "Connect your calendar"
           :tooltip    "Connect your calendar"
           :on-add     #(>e [:timeline/open-calendar-picker])}]
         [mini-apps/collapse-toggle
          {:collapsed? collapsed?
           :label      "calendar"
           :class      "app-action-btn"
           :on-toggle  #(>e [::mini-apps-data/toggle-collapsed :calendar])}]]]
       (when-not collapsed?
         [:<>
          [cal-card/broken-connections-banner]
          (cond
            loading? nil
            (not active?) [cal-card/connect-advert]
            (empty? meetings) [:div.app-placeholder "No meetings today"]
            :else
            [:div
             (when (seq upcoming)
               [mini-apps/section-view
                {:label "Upcoming" :item-count (count upcoming)
                 :open? (contains? open :upcoming) :on-toggle #(toggle-section :upcoming)}
                (cal-card/meeting-list-view upcoming {:cross-highlight? true})])
             (when (seq past)
               [mini-apps/section-view
                {:label "Recent meetings" :item-count (count past)
                 :open? (contains? open :recent) :on-toggle #(toggle-section :recent)}
                (cal-card/meeting-list-view past {:cross-highlight? true})])])])])))
